/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2026, 2026, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import {
  DigitalAssistantStatusService
} from '@app/di/integrations/digital-assistant/digital-assistant-status.service';
import { IntegrationStatus } from '@app/di/integrations/viewer/ev/enterprise-viewer-status.service';
import { filter, take } from 'rxjs/operators';
import { Logger, MessageService } from '@core/lib/services';
import { DiConfigService } from '@app/di/di-config.service';
import { appConstants } from '@app/common/app-constants';
import { UtilService } from '@app/util/util.service';
import { Events, EventService } from '@app/common/event';
import { FhirBuilderService } from '@app/di/channel/fhircast/model/fhir-builder.service';
import { FhirCastResource, FhirHubEvent } from '@app/di/channel/fhircast/model/fhircast-resource';
import { FhircastService } from '@app/di/channel/fhircast/fhircast.service';
import { FhircastTopicService } from '@app/di/channel/fhircast/fhircast-topic.service';
import { ApplicationConfig } from '@app/di/channel/websocket/websocket-registry';
import { IntegrationConfig } from '@app/di/data-model/di-fhircast-config';
import {
  PerformanceMonitorService,
  PerformanceStatus,
  PerformanceSource
} from '@app/ui/masthead/performance-monitor/performance-monitor.service';
import { Basic, OperationOutcome } from 'fhir/r4';
import { SignOutService } from '@app/common/sign-out.service';
import { CookieService } from 'ngx-cookie-service';
import { ModalsService } from '@app/common/modals/services/modals.service';
import { DaAuditService } from '@app/di/integrations/digital-assistant/da-audit.service';

export enum UpdateResponse {
  HEALTH = 'health',
  SYNC_ERROR = 'syncError'
}

export enum FhirResponseType {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  TIMEOUT = 'TIMEOUT'
}

type SocketEvent = Event | MessageEvent | CloseEvent;

const log = new Logger('DigitalAssistantService');

@Injectable({
  providedIn: 'root'
})
export class DigitalAssistantService {
  /**
   * Emits the full DA URL (with hub.topic + query params) each time a
   * FHIRcast subscription is (re-)established. The DaWindowComponent
   * subscribes to this to load the DA app in its embedded iframe.
   */
  private readonly _daUrlSubject = new ReplaySubject<string>(1);
  public readonly daUrl$ = this._daUrlSubject.asObservable();

  // modal opened tracker to avoid multiple confirmation modals
  private modalOpened = false;
  private unhealthyRelaunchAttempt = 0;
  private _topic: string;
  private openendContextsMap = new Map<number, string>();
  private hasPassedInitialHomeOpen = false;
  // True once the DA window has been shown for the first time. Study-open events
  // in afterEvents() are suppressed until this is set — if the DA window is not
  // open, imagingstudy-select would be sent before the DA iframe is ready and
  // the message would be lost. syncActiveContextToAssistant() (called on iframe
  // load) handles the deferred publish instead.
  private _daWindowOpen = false;
  private lastEvent: {
    event: FhirHubEvent;
    reqId: string;
    inProgress: boolean;
    result: boolean;
    context?: any;
    isAutoAdvance?: boolean;
    id?: number;
    promise?: Promise<any>;
    resolve?: (value: any) => void;
    reject?: (reason?: any) => void;
    timer?: any;
  } = null;

  private integrationConfig: IntegrationConfig = null;

  constructor(
    private diConfigService: DiConfigService,
    private digitalAssistantStatusService: DigitalAssistantStatusService,
    private modalsService: ModalsService,
    private eventService: EventService,
    private fhirBuilder: FhirBuilderService,
    private fhirCastService: FhircastService,
    private util: UtilService,
    private messageService: MessageService,
    private signOutService: SignOutService,
    private cookieService: CookieService,
    private daAuditService: DaAuditService,
    private fhircastTopicService: FhircastTopicService
  ) {}

  public init() {
    console.log('[DA-DEBUG] DigitalAssistantService.init() called');
    log.info('DigitalAssistantService.init() starting');
    this.integrationConfig = this.diConfigService.integrationConfig;
    console.log('[DA-DEBUG] DigitalAssistantService.init() — integrationConfig.Da:', this.integrationConfig?.Da ? JSON.stringify(this.integrationConfig.Da) : 'N/A');
    this.createSubscription();
    this.afterEvents();

    this.fhircastTopicService.topicRenewed$
      .pipe(filter(sourceId => sourceId !== appConstants.APP_ID.DA))
      .subscribe(() => {
        if (this.digitalAssistantStatusService.getIntegrationStatus() === IntegrationStatus.FAILED) {
          return;
        }
        if (this.lastEvent?.timer) {
          clearTimeout(this.lastEvent.timer);
        }
        this.lastEvent = null;
        this.openendContextsMap.clear();
        this.digitalAssistantStatusService.setLaunchedExamId(0);
        this.closeAssistant();
        this.createSubscription();
      });

    this.signOutService.registerCallback(this.cleanup);
  }

  private createSubscription() {
    this._topic = this.fhircastTopicService.currentTopic;
    this.hasPassedInitialHomeOpen = false;
    this.digitalAssistantStatusService.systemStatusListening$.pipe(filter(Boolean), take(1)).subscribe(
      () => {
        console.log('[DA-DEBUG] createSubscription() — systemStatusListening$ emitted, calling assistantLoading()');
        this.digitalAssistantStatusService.assistantLoading();
      },
      _ => log.error('Listening if system status ready is failed')
    );
    const fhircast = { ...appConstants.FHIRCAST_DEFAULT_CONFIG, ...this.integrationConfig?.Fhircast };
    const appConfig: ApplicationConfig = {
      id: appConstants.APP_ID.DA,
      hubEvents: [FhirHubEvent.SYNC_SUCCESS, FhirHubEvent.SYNC_ERROR],
      useSecureProtocol: fhircast.WebSocketUseSecureProtocol,
      secureProtocolPrefix: fhircast.WebSocketProtocolPrefix,
      secureProtocolTokenPrefix: fhircast.WebSocketProtocolTokenPrefix,
      retryPeriod: fhircast.RetryPeriod,
      maxRetry: fhircast.RetryMaxcount,
      handlers: {
        onOpen: (event: Event) => this.socketEventHandler(event),
        onMessage: (event: MessageEvent) => this.socketEventHandler(event),
        onClose: (event: CloseEvent, isRetry) => this.socketEventHandler(event, isRetry)
      }
    };
    this.fhirCastService.register(this._topic, appConfig).catch(error => {
      log.error('Fhircast Registration:', `Topic: ${this._topic} `, 'Error: ', error);
      console.error(`[WFM->DA] FHIRcast registration failed. topic=${this._topic}`, error);
      this.digitalAssistantStatusService.failedIntegrationStatus(
        IntegrationStatus.FAILED,
        'fhir.integration.WFM->DA.error.connectionFailed',
        true
      );
      this.closeAssistant();
    });
  }

  private socketEventHandler(event: SocketEvent, isRetry = false) {
    switch (event.type) {
      case 'open':
        log.info(`WFM->DA ↔ Hub WebSocket open: topic=${this._topic}`);
        console.log(`[WFM->DA] FHIRcast WebSocket connected successfully. topic=${this._topic}`);
        this.digitalAssistantStatusService.readyIntegrationStatus();
        this.launchAssistant(this._topic);
        break;
      case 'message':
        const messageEvent: MessageEvent = <MessageEvent>event;
        const message = messageEvent.data && UtilService.deserializeString(messageEvent.data);
        log.info(`WFM->DA ← Hub Received: event=${message?.event?.['hub.event'] ?? 'unknown'} id=${message?.id ?? 'unknown'}`);
        this.handleEventMessage(message);
        break;
      case 'close':
        log.info(`WFM->DA ↔ Hub WebSocket close: topic=${this._topic} isRetry=${isRetry}`);
        if (isRetry) {
          console.log(`[WFM->DA] FHIRcast WebSocket closed — retrying connection. topic=${this._topic}`);
          this.digitalAssistantStatusService.assistantLoading();
        } else {
          console.warn(`[WFM->DA] FHIRcast WebSocket closed — max retries exhausted or unrecoverable. topic=${this._topic}`);
          this.notifyUserRetry();
        }
        break;
    }
  }

  private handleEventMessage(message: any) {
    const { event } = message;
    if (event && event['hub.event']?.includes('sync')) {
      this.handleSyncResponse(message);
      return;
    }

    // Handle external home-open events from the digital assistant (DA user navigated home)
    const { SYNC_SUCCESS, HOME_OPEN } = FhirHubEvent;
    if (message?.id !== this.lastEvent?.reqId && message?.event?.['hub.event'] === HOME_OPEN) {
      this.sendAcknowledgement(message.id);
      const tab = this.util.getActiveTab();
      if (tab && tab.id) {
        this.lastEvent = {
          event: HOME_OPEN,
          reqId: message?.id,
          result: true,
          inProgress: false,
          id: tab?.workItem?.id
        };
        this.handleOpenedContexts(tab?.workItem, HOME_OPEN);
        this.messageService.broadcast('lineShaft.viewerClosed', tab.id);
        this.daAuditService.logHomeOpenReceived();
        log.info(`DA-initiated home-open: id=${message?.id} workItem=${tab?.workItem?.id} — publishing syncsuccess`);
        const fhirResource = this.fhirBuilder.build(
          null,
          this._topic,
          SYNC_SUCCESS,
          null,
          HOME_OPEN,
          message?.id
        );
        this.fhirCastService.publish(fhirResource);
      }
    }
  }

  private async launchAssistant(topic: string) {
    const { Da } = this.integrationConfig;
    let baseUrl = (Da?.url || '').trim();

    const products = this.getDaProducts(Da?.productVersions);
    const urlParams = new URLSearchParams();
    urlParams.set('hub.topic', topic);
    urlParams.set('driveApp', appConstants.APP_ID.WFM);
    urlParams.set('correlationId', UtilService.getUUID());
    try {
      const token = await this.diConfigService.getJwtToken();
      if (token) {
        urlParams.set('accessToken', token);
      }
    } catch (e) {
      log.error('Failed to fetch JWT for DA launch:', e);
    }
    const productQuery = products.map(p => `product=${p}`).join('&');
    const query = [productQuery, urlParams.toString()].filter(Boolean).join('&');
    const daUrl = `${baseUrl}?${query}`;
    log.info('Launching Digital Assistant with URL:', daUrl);
    // Emit the URL to DaWindowComponent instead of opening a browser popup.
    this._daUrlSubject.next(daUrl);
  }

  private getDaProducts(products?: string | string[]): string[] {
    if (!products) {
      return [];
    }

    const normalizedProducts = Array.isArray(products) ? products : [products];
    return normalizedProducts.map(product => encodeURIComponent(product?.trim())).filter(Boolean);
  }

  private notifyUserRetry() {
    const maxRetry = this.integrationConfig?.Da?.DaLaunchRetryMaxCount ?? appConstants.FHIRCAST_DEFAULT_CONFIG.EvLaunchRetryMaxCount;
    if (this.unhealthyRelaunchAttempt == maxRetry) {
      this.unhealthyRelaunchAttempt = 0;
      console.error(`[DA] FHIRcast connection failed permanently after ${maxRetry} retry attempts. Closing assistant.`);
      this.closeAssistant();
      this.digitalAssistantStatusService.failedIntegrationStatus(
        IntegrationStatus.FAILED,
        'fhir.integration.da.error.connectionFailed',
        true
      );
      return;
    }
    this.unhealthyRelaunchAttempt++;
    console.warn(`[DA] FHIRcast connection lost — prompting user to retry. Attempt ${this.unhealthyRelaunchAttempt} of ${maxRetry}.`);
    this.openModal(UpdateResponse.HEALTH);
  }

  private async openModal(modalType: UpdateResponse): Promise<'YES' | 'NO' | void> {
    if (this.modalOpened) {
      return;
    }
    this.modalOpened = true;
    const modelResponse = await this.modalsService.openDaErrorModal(modalType as any);
    this.modalOpened = false;
    if (modalType === UpdateResponse.SYNC_ERROR) {
      return modelResponse;
    }

    if (modelResponse === 'YES') {
      this.openendContextsMap.clear();
      this.digitalAssistantStatusService.setLaunchedExamId(0);
      if (this.lastEvent?.timer) {
        clearTimeout(this.lastEvent.timer);
      }
      this.lastEvent = null;
      this.fhircastTopicService.renewTopic(appConstants.APP_ID.DA);
      this.closeAssistant();
      this.createSubscription();
    } else {
      this.closeAssistant();
      this.digitalAssistantStatusService.failedIntegrationStatus(
        IntegrationStatus.FAILED,
        'fhir.integration.da.error.connectionFailed',
        false
      );
    }
    return modelResponse;
  }

  closeAssistant() {
    this.fhirCastService.unregister(appConstants.APP_ID.DA);
  }

  private afterEvents() {
    // Study-open events: notify DA of the newly selected study via imagingstudy-select.
    // Only fire if the DA window is already open; if it is not, the imagingstudy-select
    // would be sent before the DA iframe is ready and the message would be silently lost.
    // syncActiveContextToAssistant() (called on iframe load) handles the deferred publish.
    [Events.preview.name, Events.read.name, Events.launchreadonly.name].forEach(
      event => {
        this.eventService.afterEvent(event, async (context: any) => {
          if (this._daWindowOpen) {
            console.log('Event fired:', event, context);
            await this.open(context);
          }
        });
      }
    );
    // External tab: navigate DA back to the home context when the user opens the external tab.
    this.eventService.afterEvent(Events.externalAccessTab.name, async () => {
      if (this._daWindowOpen) {
        console.log('External tab opened, navigating DA back to home context');
        await this.home();
        console.log('Navigated DA back to home context');
      }
    });
    // Study-close events: navigate DA back to the home context via home-open.
    // Two complementary triggers cover all tab-close paths:
    //   1. closediscard/clear afterEvents — fired when the server-side close succeeds
    //      (readCloseWorkItems path). Context id may be undefined due to wrapper objects;
    //      HOME_OPEN is still sent and all open contexts are cleared.
    //   2. examTab.closed — broadcast by processCloseDiagnosticHubTab for ALL tab
    //      removals, including the diagnosticHubCloseWorkItems path (exams assigned to
    //      others, associated exams) where closediscard is never sent. The redundancy
    //      check in isRedundantEvent prevents a double HOME_OPEN when both fire.
    [Events.closediscard.name, Events.clear.name].forEach(
      event => {
        this.eventService.afterEvent(event, async (context: any) => {
          console.log('Event fired:', event, context);
          await this.close(context);
        });
      }
    );
    this.messageService.on('examTab.closed').subscribe((tabId: number) => {
      console.log('examTab.closed', { id: tabId });
      this.close({ id: tabId });
    });
  }

  public home(isAutoAdvance = false) {
    return this.sendFhircastPublish(null, FhirHubEvent.HOME_OPEN, isAutoAdvance);
  }

  public closeAll() {
    return this.sendFhircastPublish(null, FhirHubEvent.HOME_OPEN);
  }

  public close(context: any, isAutoAdvance = false) {
    return this.sendFhircastPublish(context, FhirHubEvent.HOME_OPEN, isAutoAdvance);
  }

  public open(context: any) {
    console.log('Opening DA with context:', context);
    return this.sendFhircastPublish(context, FhirHubEvent.IMAGINGSTUDY_SELECT);
  }

  public notifyDaWindowOpened(): void {
    this._daWindowOpen = true;
    // Push the active study context to DA now that the window is visible to the
    // user. onIframeLoad only fires on src changes (initial load + topic renewal),
    // not on show/hide toggling — without this call opening DA after an exam is
    // already active would show the user a blank DA home screen with no context.
    this.syncActiveContextToAssistant();
  }

  public syncActiveContextToAssistant() {
    // Only sync when the DA window is actually visible. During the initial
    // visibility:hidden preload _daWindowOpen is still false so this is a no-op;
    // the real sync happens via notifyDaWindowOpened() when the user opens the window.
    if (!this._daWindowOpen) {
      return true;
    }

    const activeWorkItem = this.util.getActiveTab()?.workItem;

    if (!activeWorkItem?.id) {
      return true;
    }

    if (
      this.hasPassedInitialHomeOpen &&
      this.digitalAssistantStatusService.getLaunchedExamId() === activeWorkItem.id
    ) {
      return true;
    }

    return this.open(activeWorkItem);
  }

  public logout() {
    return this.sendFhircastPublish(null, FhirHubEvent.USER_LOGOUT);
  }

  private logPerformance(event: FhirHubEvent, id: string | number, status: PerformanceStatus) {
    if (event === FhirHubEvent.IMAGINGSTUDY_SELECT) {
      PerformanceMonitorService.stop(PerformanceSource.DA, id, status);
    }
  }

  private auditDaEvent(event: FhirHubEvent, success: boolean) {
    switch (event) {
      case FhirHubEvent.IMAGINGSTUDY_SELECT:
        success ? this.daAuditService.logStudySelectSuccess() : this.daAuditService.logStudySelectFailure();
        break;
      case FhirHubEvent.HOME_OPEN:
        success ? this.daAuditService.logHomeOpenSuccess() : this.daAuditService.logHomeOpenFailure();
        break;
      case FhirHubEvent.USER_LOGOUT:
        this.daAuditService.logUserLogout();
        break;
    }
  }

  private shoudSkipPublish(context: any, event: FhirHubEvent) {
    return !this.digitalAssistantStatusService.isIntegrationReady() || this.isRedundantEvent(event, context?.id);
  }

  private async sendFhircastPublish(context: any, event: FhirHubEvent, isAutoAdvance = false, isRetry = false, awaitHomeOpenResponse = false) {
    try {
      if (event === FhirHubEvent.IMAGINGSTUDY_SELECT) {
        // Always send home-open first and wait for DA's sync response before proceeding,
        // regardless of whether a previous home-open has already been completed. This ensures
        // DA has fully reset to the home context before receiving the new study context.
        const homeOpenPassed = await this.sendFhircastPublish(null, FhirHubEvent.HOME_OPEN, isAutoAdvance, false, true);
        if (!homeOpenPassed) {
          return false;
        }
      } else if (event !== FhirHubEvent.HOME_OPEN && !this.hasPassedInitialHomeOpen) {
        const initialHomeOpenPassed = await this.sendFhircastPublish(null, FhirHubEvent.HOME_OPEN, isAutoAdvance);
        if (!initialHomeOpenPassed) {
          return false;
        }
      }

      if (this.lastEvent?.inProgress) {
        const progressiveEvent = await this.handleInprogressEvent(context, event, isAutoAdvance);
        if (!progressiveEvent) return false;
      }

      // For an awaited HOME_OPEN (prerequisite before imagingstudy-select) we must always
      // publish — bypass redundancy so DA actually confirms the home-context reset even
      // when openendContextsMap is empty (e.g. first launch with a study already active).
      if (event === FhirHubEvent.HOME_OPEN && awaitHomeOpenResponse) {
        if (!this.digitalAssistantStatusService.isIntegrationReady()) return true;
      } else if (this.shoudSkipPublish(context, event)) {
        return true;
      }
      PerformanceMonitorService.clear(PerformanceSource.DA);
      const isBlockingEvent = this.isBlockingEvent(event);
      let correlationId = null;
      if (event === FhirHubEvent.IMAGINGSTUDY_SELECT) {
        correlationId = UtilService.getUUID();
        this.openendContextsMap.set(context.id, correlationId);
        PerformanceMonitorService.start(PerformanceSource.DA, context.id);
      }

      const targetApp = event === FhirHubEvent.HOME_OPEN ? appConstants.APP_ID.DA : undefined;
      const fhir: FhirCastResource = this.fhirBuilder.build(context, this._topic, event, correlationId, undefined, undefined, targetApp);
      log.info(`WFM → DA Publishing: event=${event} fhirId=${fhir.id}`, UtilService.maskFhircastResource(fhir));
      this.digitalAssistantStatusService.assistantInprogress();
      isBlockingEvent && UtilService.changeCursor('progress');
      const responsePromise = this.createResponsePromise(event, fhir.id, context, isAutoAdvance);
      await this.fhirCastService.publish(fhir, appConstants.APP_ID.DA);
      this.sendAcknowledgement(this.lastEvent.reqId);
      if (event === FhirHubEvent.IMAGINGSTUDY_SELECT) {
        this.digitalAssistantStatusService.setLaunchedExamId(context.id);
      }

      if (event === FhirHubEvent.HOME_OPEN) {
        // Fire-and-forget for all home-open calls: update context state immediately
        // so the caller is never blocked. Reset integration status right away so the
        // UI remains responsive while DA processes the event in the background.
        this.handleOpenedContexts(context, event);
        this.auditDaEvent(event, true);
        this.digitalAssistantStatusService.readyIntegrationStatus();
        responsePromise.finally(() => {
          // Promise settling is a no-op for status/cursor — already reset above.
        });
        return true;
        // TODO: re-enable waiting for sync-success on home-open when DA reliably responds.
        // if (!awaitHomeOpenResponse) {
        //   this.handleOpenedContexts(context, event);
        //   this.auditDaEvent(event, true);
        //   responsePromise.finally(() => {
        //     UtilService.changeCursor('default');
        //     this.digitalAssistantStatusService.readyIntegrationStatus();
        //   });
        //   return true;
        // }
        // // Awaited prerequisite before imagingstudy-select: wait for DA's actual sync response
        // // so we know DA has reset to the home context before the new study context is sent.
        // const syncResponse = await responsePromise;
        // UtilService.changeCursor('default');
        // this.digitalAssistantStatusService.readyIntegrationStatus();
        // if (syncResponse === FhirResponseType.SUCCESS) {
        //   this.handleOpenedContexts(context, event);
        //   this.auditDaEvent(event, true);
        //   return true;
        // }
        // this.auditDaEvent(event, false);
        // return false;
      }

      const syncResponse = await responsePromise;
      this.digitalAssistantStatusService.readyIntegrationStatus();
      isBlockingEvent && UtilService.changeCursor('default');
      const msg = syncResponse === FhirResponseType.SUCCESS ? 'SyncSuccess' : 'SyncError';
      log.info(`DA → WFM SyncResponse: ${msg} event=${event} fhirId=${fhir.id}`);
      if (syncResponse === FhirResponseType.SUCCESS) {
        this.handleOpenedContexts(context, event);
        this.logPerformance(event, this.lastEvent.id, PerformanceStatus.SUCCESS);
        this.auditDaEvent(event, true);
        return true;
      }
      this.logPerformance(event, this.lastEvent.id, PerformanceStatus.ERROR);
      if (isRetry) {
        return false;
      }
      this.auditDaEvent(event, false);
      return await this.handleFalureStatus(IntegrationStatus.SYNC_ERROR, 'fhir.integration.da.error.syncError');
    } catch (error) {
      log.error(
        `Published Event Response: Error for Event: ${event} `,
        context ? `WorkItemID: ${context.id}` : '',
        error
      );
      if (this.lastEvent?.timer) {
        clearTimeout(this.lastEvent.timer);
        this.lastEvent = {
          ...this.lastEvent,
          inProgress: false,
          timer: null,
          promise: null,
          resolve: null,
          reject: null
        };
      }
      this.digitalAssistantStatusService.setLaunchedExamId(0);
      this.logPerformance(event, this.lastEvent.id, PerformanceStatus.TIMEOUT);
      UtilService.changeCursor('default');
      if (isRetry) {
        return false;
      }
      this.auditDaEvent(event, false);
      return await this.handleFalureStatus(
        IntegrationStatus.LAST_ACTION_FAILED,
        'fhir.integration.da.error.connectionFailed'
      );
    }
  }

  async handleInprogressEvent(context: any, event: FhirHubEvent, isAutoAdvance = false): Promise<boolean> {
    // home-open is fire-and-forget: cancel its pending timer and let the UI proceed
    // immediately rather than blocking the user while waiting for DA's sync response.
    if (this.lastEvent?.event === FhirHubEvent.HOME_OPEN) {
      log.debug(`Allowing ${event}: in-progress home-open is non-blocking; discarding its pending timer`);
      if (this.lastEvent.timer) {
        clearTimeout(this.lastEvent.timer);
        this.lastEvent = { ...this.lastEvent, inProgress: false, timer: null };
      }
      return true;
    }
    if (!isAutoAdvance) {
      log.debug(`Blocking ${event}: Reason: Last Event: ${this.lastEvent?.event} in progress`);
      if (!this.modalOpened) this.digitalAssistantStatusService.notifyAssistantResponsePending();
      this.messageService.broadcast('clearActivatedItem');
      return false;
    }
    const isRedundant = this.isRedundantEvent(event, context?.id);
    if (isRedundant) {
      log.debug(`Skipping Block for ${event}: Reason: Last Event: ${this.lastEvent?.event} and ${event} is redundant`);
      return true;
    }

    // Wait for the last event to respond in autoadvance workflow
    await this.lastEvent.promise;
    // Unblocking autoadvance workflow and taken care in handleFalureStatus
    return true;
  }

  private async handleFalureStatus(integrationStatus: IntegrationStatus, message: string): Promise<boolean> {
    const { event, context, isAutoAdvance } = this.lastEvent;
    try {
      if (event === FhirHubEvent.USER_LOGOUT) {
        await UtilService.executeWithDelay(appConstants.TIME.SS_FIVE, () =>
          this.handleOpenedContexts(null, FhirHubEvent.USER_LOGOUT)
        );
        return true;
      }

      if (event === FhirHubEvent.HOME_OPEN) {
        this.messageService.broadcast('clearActivatedItem');
      }

      const isBlockingEvent = this.isBlockingEvent(event);

      if (!isBlockingEvent) {
        this.digitalAssistantStatusService.failedIntegrationStatus(integrationStatus, message, false);
        return false;
      }

      if (isAutoAdvance) {
        this.launchAssistant(this._topic);
        return true;
      }

      if (integrationStatus === IntegrationStatus.SYNC_ERROR) {
        const publishResult = await this.sendFhircastPublish(context, event, isAutoAdvance, true);
        if (publishResult) {
          return true;
        }
      }

      const confirmation = await this.openModal(UpdateResponse.SYNC_ERROR);
      if (confirmation === 'YES') {
        this.openendContextsMap.clear();
        this.digitalAssistantStatusService.setLaunchedExamId(0);
        this.lastEvent = null;
        this.fhircastTopicService.renewTopic(appConstants.APP_ID.DA);
        this.closeAssistant();
        this.createSubscription();
        return true;
      }

      return false;
    } catch (error) {
      this.digitalAssistantStatusService.failedIntegrationStatus(integrationStatus, message, false);
      log.error(
        `handleFailureStatus: event: ${event}, context: ${context?.id ?? 'none'}, autoAdvance: ${isAutoAdvance}`,
        error
      );
      return false;
    }
  }

  private isBlockingEvent(event: FhirHubEvent): boolean {
    // HOME_OPEN is fire-and-forget and must not block the cursor or UI.
    return false;
  }

  private createResponsePromise(event: FhirHubEvent, reqId: string, context?: any, isAutoAdvance = false) {
    let pendingEvent;
    const promise = new Promise<FhirResponseType>((resolve, reject) => {
      pendingEvent = {
        event: event,
        reqId,
        result: false,
        context,
        isAutoAdvance,
        inProgress: true,
        id: context?.id,
        resolve,
        reject
      };

      this.lastEvent = pendingEvent;

      const requestTimeout = this.integrationConfig?.Da?.DaRequestTimeout ?? appConstants.FHIRCAST_DEFAULT_CONFIG.EvRequestTimeout;
      pendingEvent.timer = setTimeout(() => {
        log.warn(
          `Published Event Timeout: for Event: ${event}`,
          `: FHIR-Resource: ${reqId}`,
          context ? `WorkItemID: ${context}` : ''
        );
        pendingEvent.inProgress = false;
        pendingEvent.timer = null;
        if (this.lastEvent?.reqId === reqId) {
          this.lastEvent = {
            ...this.lastEvent,
            inProgress: false,
            timer: null
          };
        }
        pendingEvent.reject?.(FhirResponseType.TIMEOUT);
      }, requestTimeout);
    });
    pendingEvent.promise = promise;
    this.lastEvent = pendingEvent;
    return promise;
  }

  private handleSyncResponse(message: FhirCastResource) {
    try {
      const { event } = message;
      const isSyncSuccess = event?.['hub.event'] === FhirHubEvent.SYNC_SUCCESS;
      let operationOutcome: OperationOutcome;
      let corelationResource: Basic;

      for (const context of event?.context || []) {
        if (context?.resource['resourceType'] === 'OperationOutcome') {
          operationOutcome = <OperationOutcome>context.resource;
        } else if (context?.resource['resourceType'] === 'Basic') {
          corelationResource = <Basic>context.resource;
        }
      }

      // const responderAppId = this.getResponderAppId(operationOutcome, message);
      // if (responderAppId && responderAppId !== appConstants.APP_ID.DA) {
      //   log.debug(
      //     `Ignoring sync response for ${appConstants.APP_ID.DA}: responder=${responderAppId} messageId=${message.id}`,
      //     `Last Event ID : ${this.lastEvent?.reqId} Event: ${this.lastEvent?.event}`
      //   );
      //   return;
      // }

      const resourceCode = operationOutcome?.issue?.[0]?.details?.coding?.[0]?.code;
      if (resourceCode !== this.lastEvent?.reqId) {
        log.debug(
          `Skipping unrelated message for ID: ${message.id} Event: ${message?.event?.['hub.event']}`,
          `Last Event ID : ${this.lastEvent?.reqId} Event: ${this.lastEvent?.event}`
        );
        this.sendAcknowledgement(message.id, corelationResource?.identifier?.[0]?.value || '');
        return;
      }

      if (resourceCode === this.lastEvent?.reqId) {
        this.lastEvent.result = isSyncSuccess;
        this.lastEvent.inProgress = false;
        log.info(`DA → WFM SyncResponse matched: event=${message?.event?.['hub.event']} reqId=${resourceCode} isSyncSuccess=${isSyncSuccess}`);
        if (isSyncSuccess) {
          this.daAuditService.logSyncSuccess();
          this.sendAcknowledgement(message.id, corelationResource?.identifier?.[0]?.value || '');
        } else {
          this.daAuditService.logSyncError();
        }
        this.lastEvent?.resolve?.(isSyncSuccess ? FhirResponseType.SUCCESS : FhirResponseType.ERROR);
        if (this.lastEvent.timer) {
          clearTimeout(this.lastEvent.timer);
          this.lastEvent.timer = null;
        }
      }
    } catch (error) {
      log.error('HandleSyncResponse:', error);
    }
  }

  private getResponderAppId(operationOutcome?: OperationOutcome, message?: FhirCastResource): string | null {
    const responseAppId = operationOutcome?.extension?.find(extension => extension?.url === 'appId')?.valueString;
    const messageAppId = (message as any)?.appId || (message as any)?.applicationId;
    const normalizedAppId = responseAppId || messageAppId;
    return normalizedAppId ? normalizedAppId.toUpperCase() : null;
  }

  private sendAcknowledgement(id: string, correlationId?: string) {
    const ackMessage = {
      id,
      status: '200',
      ...(correlationId && { correlationId })
    };
    this.fhirCastService.sendNotification(appConstants.APP_ID.DA, JSON.stringify(ackMessage));
  }

  private handleOpenedContexts(context: any, event: FhirHubEvent) {
    switch (event) {
      case FhirHubEvent.HOME_OPEN:
        this.hasPassedInitialHomeOpen = true;
        if (context?.id) {
          // close(context) path — remove only the specific study
          if (this.openendContextsMap.has(context.id)) {
            this.openendContextsMap.delete(context.id);
          }
          if (this.digitalAssistantStatusService.getLaunchedExamId() === context.id) {
            this.digitalAssistantStatusService.setLaunchedExamId(0);
          }
        } else {
          // home() or closeAll() path — clear all open contexts
          this.openendContextsMap.clear();
          this.digitalAssistantStatusService.setLaunchedExamId(0);
        }
        break;
      case FhirHubEvent.USER_LOGOUT:
        this.openendContextsMap.clear();
        this.digitalAssistantStatusService.logoutInProgress = true;
        this.digitalAssistantStatusService.setLaunchedExamId(0);
        this.closeAssistant();
    }
  }

  private isRedundantEvent(incomingEvent: FhirHubEvent, id?: number) {
    // Fast track for no context scenarios
    const noOpenContexts = !this.openendContextsMap.size;

    // Handle early cases without last event
    if (!this.lastEvent) {
      return incomingEvent === FhirHubEvent.HOME_OPEN;
    }

    // Duplicate workitem event check (high priority)
    if (this.lastEvent.id && this.lastEvent.id === id && this.lastEvent.event === incomingEvent) {
      return this.lastEvent.result;
    }

    // Event-specific redundancy rules
    switch (incomingEvent) {
      case FhirHubEvent.HOME_OPEN:
        // If a specific study ID is supplied but DA was never notified about it
        // (not in openendContextsMap), sending HOME_OPEN serves no purpose.
        if (id != null && !this.openendContextsMap.has(id)) {
          return true;
        }
        return noOpenContexts || (this.lastEvent.event === FhirHubEvent.HOME_OPEN && this.lastEvent.result);

      default:
        return false;
    }
  }

  private cleanup = () => {
    this.hasPassedInitialHomeOpen = false;
    this.fhirCastService.unregister(appConstants.APP_ID.DA);
  };
}
